// Socket
import socket from "../../../socket/socket";
import { authorize } from "../../../socket/auth";
import router from "@/router";

// Axios
import axios from "axios";

// Timeout for logging user out
let logoutTimer = false;

/**
 * Required for web push
 * @param {*} base64String
 */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    // eslint-disable-next-line no-useless-escape
    .replace(/\-/g, "+")
    .replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Adding handler
 * @param {Event} e
 */
let deferred = null;
function handler(e) {
  // Prevent the mini-infobar from appearing on mobile
  e.preventDefault();
  // Stash the event so it can be triggered later.
  deferred = e;
  // Remove the event listener
  window.removeEventListener("beforeinstallprompt", handler);
}
window.addEventListener("beforeinstallprompt", handler);

/**
 * Show the add to home screen prompt
 * @param {Object} context
 */
function addedToHomeScreenHandler(context) {
  // New function to replace
  function handlerNew(e) {
    // Prevent the mini-infobar from appearing on mobile
    e.preventDefault();
    // Stash the event so it can be triggered later.
    context.commit("setAddToHomeEvent", e);
    // Update UI notify the user they can install the PWA
    context.commit("setShowAddToHomePopup", true);
    // Remove the event listener
    window.removeEventListener("beforeinstallprompt", handlerNew);
  }

  // Update UI notify the user they can install the PWA
  if (deferred) {
    context.commit("setShowAddToHomePopup", true);
    context.commit("setAddToHomeEvent", deferred);
  } else {
    window.removeEventListener("beforeinstallprompt", handler);
    window.addEventListener("beforeinstallprompt", handlerNew);
  }
}

/**
 * Notification register
 * @param {Object} context
 */
async function notificationPermissionHandler(context) {
  context.commit("setShowNotificationPopup", true);
}

/**
 * Function to handle notification
 */
async function handleNotification(context) {
  const notificationPermission = localStorage.getItem(
    "notificationPermission"
  );
  if (notificationPermission === null) {
    notificationPermissionHandler(context);
  }

  if('accepted' === notificationPermission) {
    await context.dispatch("subscribeUser");
  }
}

/**
 * Actions for auth
 */
export const actions = {
  /**
   *
   * @param {Object} context
   * @param {Object} payload
   */
  async login(context, payload) {
    if (!context.state.loadingAuth) {
      // Start loading screen
      context.commit("setLoadingAuth", true);

      return new Promise(async (resolve, reject) => {
        try {
          // Step 1: Create a fetch "POST" request and get async response
          const res = await axios("/" + "auth/login", {
            method: "POST", // *GET, POST, PUT, DELETE, etc.
            mode: "no-cors", // no-cors, cors, *same-origin
            cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
            credentials: "same-origin", // include, *same-origin, omit
            headers: {
              "Content-Type": "application/json; charset=utf-8"
            },
            redirect: "follow", // manual, *follow, error
            referrer: "no-referrer", // no-referrer, *client
            data: JSON.stringify(payload) // body data type must match "Content-Type" header
          });

          /**
           * Response format
           * code: 200
           * data: {token: "716417d3b55782c4998be53b8af3dad6", userID: 2213, timeRemain: 1598819134}
           * timeRemain: 1598819134
           * token: "716417d3b55782c4998be53b8af3dad6"
           * userID: 2213
           * success: "successfully logged in"
           */
          const json = res.data;

          // Setting state variables
          context.commit("setUsername", payload.username);
          context.commit("setToken", json.data.token);
          context.commit("setUserId", json.data.userID);
          context.commit("setMessage", "");

          const session = {
            userId: json.data.userID,
            token: json.data.token
          };
          // Adding the authentication token and userID to the session storage, which is used for authorizing users later
          window.sessionStorage.setItem("auth", JSON.stringify(session));

          // Setting timer to log user out automatically
          context.dispatch(
            "setExpiryAndPermission",
            isNaN(json.data.timeRemain) ? 0 : parseInt(json.data.timeRemain)
          );

          resolve();
          authorize(); // Socket authorize

          // Stop loading
          context.commit("setLoadingAuth", false);
        } catch (err) {
          const json = err.response.data;

          // Step 3: Check if 'error' is a defined in the response, if yes then login has failed
          if ("error" in json) {
            context.commit("setMessage", json.error);
            reject(json.error);
          }

          // Stop loading
          context.commit("setLoadingAuth", false);
        }
      });
    }
  },

  /**
   * Function for logging out
   * @param {Object} context
   */
  async logout(context) {
    let session = context.getters.userInfo;

    // Auth token required by the server
    session = {
      userId: session.userId,
      token: session.token
    };

    // Show loading
    context.commit("setGlobalLoading", true);

    // Sending logout request to server to delete session data
    await axios("/" + "auth/logout", {
      method: "GET", // *GET, POST, PUT, DELETE, etc.
      mode: "cors", // no-cors, cors, *same-origin
      cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
      credentials: "same-origin", // include, *same-origin, omit
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        token: session.token,
        userid: session.userId
      },
      redirect: "follow", // manual, *follow, error
      referrer: "no-referrer" // no-referrer, *client
    });

    // Logout request sent
    await socket.emit("logout");

    // Logout user locally
    context.commit("removeAuth");
    logoutTimer = false;

    // Hide loading
    router.push("/login");
    context.commit("setGlobalLoading", false);

    // Reload page
    location.reload();
  },

  /**
   * Setting expiry and automatically logging user out
   * @param {Oject} context
   * @param {Boolean} expiry
   */
  async setExpiryAndPermission(context, expiry = false) {
    return new Promise((resolve, reject) => {
      if (!expiry && !logoutTimer) {
        // Start global spinner
        context.commit("setGlobalLoading", true);

        // Getting expiry time from server
        context
          .dispatch("makeAuthRequest", {
            url: "auth/expiry_permissions",
            method: "GET"
          })
          .then(json => {
            // Stop global spinner
            context.commit("setGlobalLoading", false);

            // For app user set the permitted user list
            if (json.userRole === 24) {
              if (!json.permittedUserList) {
                json.permittedUserList = [];
              }

              // Step I: Adding first name as default subscriber id
              json["permittedUserList"].unshift({
                id: json["subscriber_id"],
                permission_id: json["permission_id"],
                permission_status: json["permission_status"],
                firstName: json.firstName,
                lastName: json.lastName,
                middleName: json.middleName,
                namePrefix: json.namePrefix,
                nameSuffix: json.nameSuffix,
                credentials: json.credentials,
                add_video_appointment_permission:
                  json["add_video_appointment_permission"],
                delete_video_appointment_permission_refused:
                  json["delete_video_appointment_permission_refused"],
                  connectUserID: json.connectUserID,
                  videoUserID: json.videoUserID
              });

              // Step II: Adding to state
              context.commit("setPermittedUsers", json.permittedUserList);
              context.commit("setSecureVideoPermissionApp", json);
            } else if (json.userRole === 22 || json.userRole === 25) {
              context.commit("chat/setSelectedUserId", json["subscriber_id"], {
                root: true
              });
              context.commit(
                "chat/setName",
                {
                  firstName: json["subscriber_firstName"],
                  middleName: json["subscriber_middleName"],
                  lastName: json["subscriber_lastName"],
                  namePrefix: json["subscriber_namePrefix"],
                  nameSuffix: json["subscriber_nameSuffix"]
                },
                {
                  root: true
                }
              );
            } else {
              // Setting if user has permission to add video appointments for non-app users
              context.commit("setSecureVideoPermissionNonApp", json);
            }
            context.commit("portalemail/setSubscriberId", json["subscriber_id"]);
            if(0 === json["subscriber_id"]) {
              context.commit("portalemail/setAppUsers", json["appUsers"]);
            }

            if ("permission_id" in json && "permission_status" in json) {
              context.commit("setAddionalPermissionToAddEditAndDelete", {
                id: json["permission_id"],
                status: json["permission_status"]
              });
            }

            // Setting the order and format of the name
            if ("client_name_format" in json && "client_name_order" in json) {
              context.commit("setOrderAndFormatForGetAppointment", {
                clientNameFormat: json["client_name_format"],
                clientNameOrder: json["client_name_order"]
              });
            }

            context.commit("setSelectedProviderID", json);

            if (
              !json.timeRemain ||
              !("userRole" in json) ||
              isNaN(json.userRole) ||
              !("userType" in json) ||
              isNaN(json.userType)
            ) {
              // Logout user locally
              if (context.getters.checkSession) {
                context.dispatch("logout");
              }
              resolve();
              return false;
            }

            expiry = isNaN(json.timeRemain) ? 0 : parseInt(json.timeRemain);

            const timeRemain = expiry * 1000 - new Date().getTime();

            if (timeRemain >= 0) {
              context.dispatch("setTimeoutForLogout", timeRemain);
              context.commit("setUserRole", json.userRole);
              context.commit("setUsetType", json.userType);
              context.commit("setLoggedInTimezone", json.loggedInTimezone);
              context.commit("setUserDetails", json);
              context.commit("setUserSubscription", json.subscription);

              // Subscribe for push notification
              if (process.env.NODE_ENV !== "development") {
                context.dispatch("pwaPopups");
              }
            } else {
              // Logout user
              if (context.getters.checkSession) {
                context.dispatch("logout");
              }
              resolve();
              return false;
            }

            // Sets the unread Mail Ids
            if("unreadMailIds" in json) {
              context.commit("portalemail/setUnreadMailIds", {mailIds: json.unreadMailIds, setForOtherPage: true});
            }

            resolve();
          })
          .catch(err => {
            reject(err);
          });
      }
    });
  },

  async addToHomeScreenAccept(context) {
    // Step 1: Get the deffered prompt
    const deferredPrompt = context.getters.getAddToHomeEvent;

    // Step 2: Show the prompt
    deferredPrompt.prompt();

    // Step 3: Wait for the user to respond to the prompt
    deferredPrompt.userChoice.then(choiceResult => {
      if (choiceResult.outcome === "accepted") {
        localStorage.setItem("addedToHomeScreen", "accepted");
      } else {
        localStorage.setItem("addedToHomeScreen", "dont_ask");
        // handleNotification(context);
      }
    });
  },

  /**
   * Subscribe user for push notification
   */
  async subscribeUser(context) {
    // Step 1: Get the register object
    if ("serviceWorker" in navigator) {
      // Step 2: Get push subscription
      let subscription = false;
      try {
        const register = await navigator.serviceWorker.register(
          "/service-worker.js",
          {
            scope: "/"
          }
        );

        await navigator.serviceWorker.ready; // Here's the waiting for service worker to be ready

        subscription = await register.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(
            process.env.VUE_APP_PUBLIC_VAPID_KEY
          )
        });
      } catch (error) {
        // localStorage.setItem("notificationPermission", "dont_ask");
        subscription = false;
      }

      // Step 3: Send push subscription
      if (subscription) {
        let oldSubscription = context.getters.getUserSubscription;
        if(oldSubscription !== JSON.stringify(subscription)) {
          await context.dispatch("makeAuthRequest", {
            url: "subscription/add",
            method: "POST",
            chat: true,
            data: JSON.stringify(subscription),
            stringify: true
          });
          // localStorage.setItem("notificationPermission", "accepted");
          context.commit("setUserSubscription", JSON.stringify(subscription));
        }
      }
    }
  },

  /**
   * Subscribe user for push notification
   */
  async pwaPopups(context) {
    // Step 1: Check if service worker is there
    if (!("serviceWorker" in navigator)) {
      return;
    }

    // Step 2: Add to home screen and notification register
    try {
      // Step 2,1: Check if added to homescreen
      const addedToHomeScreen = localStorage.getItem("addedToHomeScreen");
      if (addedToHomeScreen === null) {
        addedToHomeScreenHandler(context);
      }/* else {
        // Step 2,2: Send notification permission
        const notificationPermission = localStorage.getItem(
          "notificationPermission"
        );
        if (notificationPermission === null) {
          notificationPermissionHandler(context);
        }

        // Step 2,3: If notification permission is already accepted, send a subscription to server
        if('accepted' === notificationPermission) {
          await context.dispatch("subscribeUser");
        }
      }*/
      await context.dispatch("subscribeUser");
    } catch (error) {
      // Do nothing
    }
  },

  /**
   * Refresh token when user is about to be logged out
   * @param {Object} context
   */
  async sendRefreshRequest(context) {
    const json = await context.dispatch("makeAuthRequest", {
      url: "auth/expiry_permissions",
      method: "GET"
    });

    if (
      !json.timeRemain ||
      !("userRole" in json) ||
      isNaN(json.userRole) ||
      !("userType" in json) ||
      isNaN(json.userType)
    ) {
      if (context.getters.checkSession) {
        context.dispatch("logout");
      }
      return false;
    } else {
      const expiry = isNaN(json.timeRemain) ? 0 : parseInt(json.timeRemain);
      const timeRemain = expiry * 1000 - new Date().getTime();

      if (timeRemain >= 0) {
        context.dispatch("setTimeoutForLogout", timeRemain);
        context.commit("setUserRole", json.userRole);
        context.commit("setUsetType", json.userType);
        context.commit("setLoggedInTimezone", json.loggedInTimezone);
        context.commit("setUserDetails", json);
      } else {
        // Logout user
        if (context.getters.checkSession) {
          context.dispatch("logout");
        }
        return false;
      }

      // Sets the unread Mail Ids
      if("unreadMailIds" in json) {
        if(!(
            (0 === context.getters["portalemail/getSubscriberId"]) &&
            (context.getters["portalemail/getSelectedUserId"] > 0)
        )) {
          context.commit("portalemail/setUnreadMailIds", {mailIds: json.unreadMailIds, setForOtherPage: true});
        }
      }
    }
  },

  /**
   * Setting timer after which user was being logged out,
   *  but now we are going to send another set expiry.
   *  Also now the refresh request is being sent 1 min before
   * @param {Object} context
   * @param {Number} timeRemain
   */
  setTimeoutForLogout(context, timeRemain) {
    if (logoutTimer) {
      clearTimeout(logoutTimer);
    }
    logoutTimer = setTimeout(() => {
      // Logout user
      context.dispatch("sendRefreshRequest");
    }, timeRemain - 1000 * 60);
  }
};
